import React, { useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Wand2, Loader2 } from "lucide-react";

/**
 * OutlineCopilot — AI shortcut that lives at the top of the wizard's
 * OUTLINE step. The author pastes a handful of bullet points describing
 * what the program should cover, the backend hands them to Gemini, and
 * the structured outline lands in `outline_text` so the wizard's next
 * steps (chapter planner, lesson generator) can pick up unchanged.
 *
 * Used by admin, instructor and SME wizards — backend route is
 * /programs/{id}/expand-outline which is open to all three roles via the
 * shared `_is_program_owner_or_admin` guard.
 */
export default function OutlineCopilot({ programId, onExpanded }) {
    const [bullets, setBullets] = useState("");
    const [busy, setBusy] = useState(false);
    const [preview, setPreview] = useState("");

    const submit = async () => {
        if (!programId) {
            toast.error("Create the program first, then run the co-pilot.");
            return;
        }
        if (bullets.trim().length < 12) {
            toast.error("Type at least a sentence or two of bullets.");
            return;
        }
        setBusy(true);
        try {
            const { data } = await api.post(
                `/programs/${programId}/expand-outline`,
                { bullets: bullets.trim() },
            );
            setPreview(data.outline || "");
            onExpanded?.(data.outline || "");
            toast.success("AI drafted your outline — review it below.");
        } catch (err) {
            const detail = err?.response?.data?.detail;
            toast.error(
                typeof detail === "string"
                    ? detail
                    : "Could not expand outline. Try again.",
            );
        } finally {
            setBusy(false);
        }
    };

    return (
        <div
            className="rounded-xl border border-accent/30 bg-accent/5 p-4 space-y-3"
            data-testid="outline-copilot"
        >
            <div className="flex items-start gap-2">
                <Wand2 className="h-4 w-4 text-accent mt-0.5" />
                <div>
                    <div className="text-sm font-semibold text-primary">
                        AI co-pilot — turn bullets into an outline
                    </div>
                    <div className="text-xs text-muted-foreground">
                        Jot down what you want the program to cover.
                        We'll fan it out into a 4-8 chapter outline you can
                        edit before generating lessons.
                    </div>
                </div>
            </div>
            <div>
                <Label htmlFor="copilot-bullets" className="text-xs">
                    Bullets
                </Label>
                <Textarea
                    id="copilot-bullets"
                    rows={4}
                    placeholder={
                        "- Cover Canadian tax basics\n" +
                        "- HST/GST registration & remittance\n" +
                        "- Practical bookkeeping with QuickBooks Online\n" +
                        "- T4/T5 slip preparation"
                    }
                    value={bullets}
                    onChange={(e) => setBullets(e.target.value)}
                    data-testid="copilot-bullets"
                    disabled={busy}
                />
            </div>
            <div className="flex items-center justify-between gap-2">
                <span className="text-[11px] text-muted-foreground">
                    Powered by Gemini · ~10 seconds
                </span>
                <Button
                    onClick={submit}
                    disabled={busy || !bullets.trim()}
                    className="bg-accent hover:bg-accent/90 text-accent-foreground gap-2"
                    data-testid="copilot-expand-btn"
                >
                    {busy ? (
                        <>
                            <Loader2 className="h-4 w-4 animate-spin" />
                            Drafting…
                        </>
                    ) : (
                        <>
                            <Wand2 className="h-4 w-4" />
                            Expand into outline
                        </>
                    )}
                </Button>
            </div>
            {preview ? (
                <div
                    className="rounded-md border border-emerald-200 bg-emerald-50/60 p-3 max-h-56 overflow-y-auto"
                    data-testid="copilot-preview"
                >
                    <div className="text-[10px] uppercase tracking-[0.18em] font-semibold text-emerald-700 mb-1">
                        AI-drafted outline (saved)
                    </div>
                    <pre className="whitespace-pre-wrap text-[12px] leading-relaxed text-foreground font-sans">
                        {preview}
                    </pre>
                </div>
            ) : null}
        </div>
    );
}
